/**
 * Recharge quick-add credential capture.
 *
 * A subscription customer arrives from a Klaviyo email with `?email=…&token=…`.
 * This script stores that pair locally so the PDP can offer "Add to subscription",
 * then removes both params from the visible URL.
 *
 * Renders nothing. Never writes the credential into the DOM and never logs it.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'wuffes_recharge_quick_add';

  /**
   * Hygiene only. The Klaviyo `recharge_quick_add_token` never expires and the
   * server is the sole authority on whether a credential is valid — this TTL just
   * stops a stale credential from lingering in a shared browser indefinitely.
   */
  const CREDENTIAL_TTL_MS = 48 * 60 * 60 * 1000;

  function parseCurrentUrl() {
    try {
      return new URL(window.location.href);
    } catch (error) {
      return null;
    }
  }

  function storeCredential(email, token) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: email, token: token, savedAt: Date.now() }));
    } catch (error) {
      // Storage unavailable (private mode, blocked cookies). Feature stays inert.
    }
  }

  /**
   * A standing credential must not persist in browser history or reach analytics,
   * so strip it from the visible URL while preserving every other param.
   */
  function stripCredentialFromUrl(url) {
    url.searchParams.delete('email');
    url.searchParams.delete('token');

    try {
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (error) {
      // replaceState unavailable — nothing else to do.
    }
  }

  /** Drop an entry that is past the hygiene TTL so it is treated as absent everywhere. */
  function pruneExpiredCredential() {
    let raw = null;

    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return;
    }

    if (!raw) return;

    let stored = null;
    try {
      stored = JSON.parse(raw);
    } catch (error) {
      stored = null;
    }

    const isUsable =
      stored && stored.email && stored.token && stored.savedAt && Date.now() - stored.savedAt <= CREDENTIAL_TTL_MS;

    if (isUsable) return;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Nothing to do.
    }
  }

  const currentUrl = parseCurrentUrl();
  if (!currentUrl) return;

  const email = currentUrl.searchParams.get('email');
  const token = currentUrl.searchParams.get('token');

  // No credential in the URL — never overwrite or clear a previously stored one.
  if (!email || !token) {
    pruneExpiredCredential();
    return;
  }

  storeCredential(email, token);
  stripCredentialFromUrl(currentUrl);
})();
